const bookedTimesCache = {};

/**
 * Fetch booked times for a given date with caching.
 * @param {string} date - The date in "YYYY-MM-DD" format.
 * @returns {Promise<Object|null>} - Booked times response from the backend or null on failure.
 */
export async function fetchBookedTimes(date) {
    if (bookedTimesCache[date]) return bookedTimesCache[date];

    try {
        const response = await fetch(`/wp-json/booking/v1/booked-times/${date}`);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);

        const data = await response.json();
        bookedTimesCache[date] = data;
        return data;
    } catch (error) {
        console.error("Failed to fetch booked times:", error);
        return null;
    }
}

/**
 * Clear cached booked times.
 */
export function clearBookedTimesCache() {
    Object.keys(bookedTimesCache).forEach((key) => delete bookedTimesCache[key]);
}

/**
 * Populate a dropdown with time options while handling booking conflicts.
 * @param {string} type - Type of dropdown ('from' or 'to').
 * @param {HTMLElement} dropdown - The dropdown element to populate.
 * @param {string} startTime - The start time in "HH:MM" format.
 * @param {string} endTime - The end time in "HH:MM" format.
 * @param {number} interval - Interval in minutes (default: 30).
 * @param {Object} backendResponse - Backend response data.
 * @param {string|null} selectedFromTime - Selected "from" time (only for 'to' dropdown).
 */
export function populateDropdown(
    type,
    dropdown,
    startTime,
    endTime,
    interval = 30,
    backendResponse,
    selectedFromTime = null
) {
    dropdown.innerHTML = ""; // Clear previous options
    dropdown.appendChild(createOption(type === "from" ? "Select start time" : "Select end time", true));

    let current = parseTimeToMinutes(startTime);
    const end = parseTimeToMinutes(endTime);
    let foundGap = false; // Track if there's a fully booked slot blocking further selections

    // Adjust "To Time" dropdown based on selected "From Time"
    if (type === "to" && selectedFromTime) {
        current = parseTimeToMinutes(formatMinutesToTime(
            parseTimeToMinutes(selectedFromTime) + interval
        ));
    }

    while (current <= end) {
        const time = formatMinutesToTime(current);
        const isFullyBooked = isGlobalConflict(time, backendResponse.fullyBookedSlots);
        const isPartiallyAvailable = isPartialConflict(time, backendResponse.partiallyAvailableSlots);
        const hasGap = type === "to" && isGapBetweenTimes(selectedFromTime, time, backendResponse.fullyBookedSlots);

        console.log(isFullyBooked, 'isFullyBooked');
        console.log(time, isFullyBooked ? "❌ Fully Booked" : "✅ Available", isPartiallyAvailable ? "🔸 Partial Availability" : "");
        console.log(backendResponse.partiallyAvailableSlots, 'partially')

        // If there’s a fully booked slot **directly between** From and To, stop adding options
        if (type === "to" && hasGap) {
            foundGap = true;
        }

        console.log(!foundGap && (!isFullyBooked || isPartiallyAvailable), 'booked')
        console.log(hasAvailableRooms(time, backendResponse.partiallyAvailableSlots), 'rooms')
        // Only allow adding "To Time" options if:
        // 1. No blocking gap exists
        // 2. The time is NOT fully booked OR is partially available
        if ((!foundGap && (!isFullyBooked || isPartiallyAvailable)) || hasAvailableRooms(time, backendResponse.partiallyAvailableSlots)) {
            dropdown.appendChild(createOption(time));
        }

        // **Do not break immediately**, allow checking further slots until a full block is confirmed
        if (foundGap) break;

        current += interval;
    }
}

function hasAvailableRooms(time, partiallyAvailableSlots) {

    return partiallyAvailableSlots.some(slot => {
        const [start, end] = slot.timeRange.split('-').map(parseTimeToMinutes);
        return parseTimeToMinutes(time) >= start && parseTimeToMinutes(time) <= end && slot.availableRooms.length > 0;
    });
}

/**
 * Checks if a fully booked slot **directly between** "From Time" and potential "To Time" exists.
 */
function isGapBetweenTimes(fromTime, toTime, fullyBookedSlots) {
    const fromMinutes = parseTimeToMinutes(fromTime);
    const toMinutes = parseTimeToMinutes(toTime);

    return fullyBookedSlots.some(slot => {
        const [slotStart, slotEnd] = slot.timeRange.split("-").map(parseTimeToMinutes);

        // Allow selection if there's a continuous flow **before** the fully booked slot
        return fromMinutes < slotStart && toMinutes > slotStart && toMinutes <= slotEnd;
    });
}

/**
 * Adjust start time logic for 'to' dropdown.
 * @param {string} selectedTime - The selected 'from' time in "HH:MM" format.
 * @param {number} interval - Interval in minutes.
 * @returns {number[]} - Adjusted [hour, minutes].
 */
function adjustStartTimeForToDropdown(selectedTime, interval) {
    const [hour, minutes] = parseTime(selectedTime);
    const totalMinutes = hour * 60 + minutes + interval;
    return [Math.floor(totalMinutes / 60), totalMinutes % 60];
}

/**
 * Validate, round time input, and update related fields.
 * @param {Event} event - Input event.
 * @param {HTMLInputElement} fromTimeInput - "From" time input element.
 * @param {HTMLSelectElement} toTimeInput - "To" time dropdown element.
 * @param {HTMLElement} durationText - Duration display element.
 * @param {Function} updateCallback - Callback to update duration.
 */
export function validateAndRoundTime(event, fromTimeInput, toTimeInput, durationText, updateCallback) {
    const input = event.target;
    const [hours, minutes] = parseTime(input.value);
    const roundedMinutes = Math.round(minutes / 30) * 30;
    input.value = formatMinutesToTime(hours * 60 + roundedMinutes);

    const { startDatetime, endDatetime, duration } = updateCallback(fromTimeInput, toTimeInput, durationText);
    document.dispatchEvent(new CustomEvent("timeUpdated", { detail: { startDatetime, endDatetime, duration } }));
}

/**
 * Check if a time conflicts globally with fully booked slots.
 * @param {string} time - Time string in "HH:MM" format.
 * @param {Array<Object>} fullyBookedSlots - Fully booked slots.
 * @returns {boolean} - True if globally fully booked, false otherwise.
 */
function isGlobalConflict(time, fullyBookedSlots) {
    return fullyBookedSlots.some(slot => {
        const [start, end] = slot.timeRange.split("-").map(parseTimeToMinutes);
        const currentTime = parseTimeToMinutes(time);
        return currentTime >= start && currentTime < end;
    });
}

/**
 * Check if a time is partially available.
 * @param {string} time - Time string in "HH:MM" format.
 * @param {Array<Object>} partiallyAvailableSlots - Partially available slots.
 * @returns {boolean} - True if partially available, false otherwise.
 */
function isPartialConflict(time, partiallyAvailableSlots) {
    return partiallyAvailableSlots.some(slot => {
        const [start, end] = slot.timeRange.split("-").map(parseTimeToMinutes);
        const currentTime = parseTimeToMinutes(time);
        return currentTime >= start && currentTime < end && slot.availableRooms.length > 0;
    });
}

/**
 * Parse a time string into total minutes from 00:00.
 * @param {string} time - Time in "HH:MM" format.
 * @returns {number} - Total minutes.
 */
function parseTimeToMinutes(time) {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
}

/**
 * Convert total minutes to a time string in "HH:MM" format.
 * @param {number} minutes - Total minutes.
 * @returns {string} - Time string in "HH:MM" format.
 */
function formatMinutesToTime(minutes) {
    const hours = Math.floor(minutes / 60).toString().padStart(2, "0");
    const mins = (minutes % 60).toString().padStart(2, "0");
    return `${hours}:${mins}`;
}

/**
 * Parse a time string into hours and minutes.
 * @param {string} time - Time in "HH:MM" format.
 * @returns {number[]} - [hours, minutes].
 */
function parseTime(time) {
    return time.split(":").map(Number);
}

/**
 * Creates an option element for a dropdown.
 * @param {string} text - The display text of the option.
 * @param {boolean} isDisabled - Whether the option should be disabled.
 * @returns {HTMLOptionElement} - The created option element.
 */
function createOption(text, isDisabled = false) {
    const option = document.createElement("option");
    option.value = isDisabled ? "" : text; // Disabled options should not have a valid value
    option.textContent = text;

    if (isDisabled) {
        option.disabled = true;
        option.selected = true; // Ensure it's pre-selected
    }

    return option;
}


/**
 * Update duration display based on selected times.
 * @param {HTMLInputElement} fromTimeInput - "From" time input element.
 * @param {HTMLSelectElement} toTimeInput - "To" time dropdown element.
 * @param {HTMLElement} durationText - Duration display element.
 * @returns {Object} - Updated duration data.
 */
export function updateDuration(fromTimeInput, toTimeInput, durationText) {
    const fromTime = fromTimeInput.value;
    const toTime = toTimeInput.value;

    if (!fromTime || !toTime) {
        durationText.textContent = "0 hours";
        return { startDatetime: null, endDatetime: null, duration: "0 hours" };
    }

    const startDatetime = new Date().toISOString().split("T")[0] + `T${fromTime}:00`;
    const endDatetime = new Date().toISOString().split("T")[0] + `T${toTime}:00`;

    const durationMinutes = calculateTimeDifferenceInMinutes(fromTime, toTime);
    const duration = durationMinutes > 0 ? formatDuration(durationMinutes) : "Invalid duration";

    durationText.textContent = duration;
    return { startDatetime, endDatetime, duration };
}

/**
 * Calculate the time difference in minutes.
 * @param {string} fromTime - Start time in "HH:MM" format.
 * @param {string} toTime - End time in "HH:MM" format.
 * @returns {number} - Time difference in minutes.
 */
function calculateTimeDifferenceInMinutes(fromTime, toTime) {
    return parseTimeToMinutes(toTime) - parseTimeToMinutes(fromTime);
}

/**
 * Format duration as a string.
 * @param {number} minutes - Duration in minutes.
 * @returns {string} - Formatted duration.
 */
function formatDuration(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours > 0 ? `${hours} hour${hours !== 1 ? "s" : ""}` : ""} ${
        mins > 0 ? `${mins} minute${mins !== 1 ? "s" : ""}` : ""
    }`.trim();
}
